/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

/**
 * Numeric nonce is most often used to compress nonce data up to several bytes long counter,
 * or even recalculate counter on both sides synchronously. This class combines (XORs) a number
 * with a byte-array nonce specified on creation. This "extends" number to the desired size.
 */
class NumericNonce(val base: UByteArray) {

    /**
     * Combines [base] by XORing its byte representation with a [base]
     * @throws NonceOutOfBoundsException if the argument is too big to be kept in the nonce. For example,
     *      nonce of 4 bytes [base] can't be combined with a number > 2^32, etc.
     */
    fun withULong(numericNonce: ULong): UByteArray {
        val result = base.copyOf()
        var x = numericNonce
        var i = 0
        while (x > 0u) {
            result[i] = result[i] xor (x and 0xFFu).toUByte()
            x = x shr 8
            if( i++ >= base.size)
                throw NonceOutOfBoundsException("this nonce has only ${base.size} bytes length and can hold nonce $numericNonce")
        }
        return result
    }

    /**
     * Combines [base] by XORing its byte representation with a [base], see [withULong]
     */
    @Suppress("unused")
    fun withLong(intNonce: Long): UByteArray {
        require( intNonce >= 0 )
        return withULong(intNonce.toULong())
    }

    /**
     * Combines [base] by XORing its byte representation with a [base], see [withULong]
     */
    fun withInt(intNonce: Int): UByteArray {
        require( intNonce >= 0 )
        return withULong(intNonce.toULong())
    }


    /**
     * Combines [base] by XORing its byte representation with a [base], see [withULong]
     */
    @Suppress("unused")
    fun withUInt(intNonce: UInt): UByteArray {
        return withULong(intNonce.toULong())
    }

    companion object {
        /**
         * Create a random numeric nonce with a specified size.
         */
        fun random(size: Int) = NumericNonce(randomUBytes(size))
    }
}
